import router from '@/router';
import createKindeClient, { KindeClient } from '@kinde-oss/kinde-auth-pkce-js';
import { useAuthStore } from '@/stores/authStore';

let kindeClient: any;

const handleAuthFailure = async () => {
  console.log('Authentication failed. Redirecting to home page.');
  const authStore = useAuthStore();
  await authStore.logout();
};

let refreshPromise: Promise<string> | null = null;
const REFRESH_THRESHOLD = 60; // Refresh token if less than 60 seconds until expiry


export const initializeKindeClient = async () => {
  kindeClient = await createKindeClient({
    client_id: import.meta.env.VITE_AUTH_CLIENTID,
    domain: import.meta.env.VITE_AUTH_DOMAIN as string,
    redirect_uri: window.location.origin + '/auth/callback',
    logout_uri: window.location.origin + '/loggedout',
    audience: import.meta.env.VITE_AUTH_API_AUDIENCE,
    is_dangerously_use_local_storage: true,
    on_redirect_callback: async (user, appState) => {
      if (user) {
      } else {
        console.log('Login Failed');
        await handleAuthFailure();
      }
    }
  });
};

export const getKindeClient = () => kindeClient;

export const getTokenExpiration = (token: string): number | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    return payload.exp || null;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};


// refresh the token if it's close to expiring 
export const getTokenWithRefresh = async (): Promise<string | null> => {
  if (!kindeClient) {
    await handleAuthFailure();
    return null;
  }
  
  try {
    const token = await kindeClient.getToken();
    if (!token) {
      await handleAuthFailure();
      return null;
    }

    const expiresAt = getTokenExpiration(token);    
    if (shouldRefreshToken(expiresAt)) {
      const refreshedToken = await silentRefresh();
      if (!refreshedToken) {
        await handleAuthFailure();
        return null;
      }
      return refreshedToken;
    }
    
    return token;
  } catch (error) {
    console.error('Error getting token:', error);
    await handleAuthFailure();
    return null;
  }
};

const silentRefresh = async (): Promise<string | null> => {
  try {
    if (!refreshPromise) {
      refreshPromise = kindeClient.getToken({ ignoreCache: true })
        .finally(() => { refreshPromise = null; });
    }
    const token = await refreshPromise;
    if (!token) {
      await handleAuthFailure();
      return null;
    }
    return token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    await handleAuthFailure();
    return null;
  }
};

const shouldRefreshToken = (expiresAt: number | null): boolean => {
  if (!expiresAt) return true;
  const now = Math.floor(Date.now() / 1000);
  return expiresAt - now < REFRESH_THRESHOLD;
};
